import { PdfImportController } from '@/controllers/admin/pdfImport'
import {
  Bounds,
  Illustration,
  IllustrationRowState,
  IllustrationsSaveRequest
} from '@/interfaces/admin/pdfImport'
import { Module } from 'vuex'
import { postSuccess, postError } from '@/helpers/notification'
import i18n from '@/locales'

function generateClientKey(): string {
  return `ill_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
}

function illustrationToRowState(illustration: Illustration): IllustrationRowState {
  return {
    ...illustration,
    bounds: { ...illustration.bounds },
    _boundsChanged: false,
    _clientKey: generateClientKey(),
    _isNew: false,
    _previewUrl: null
  }
}

function bustImageUrl(url: string): string {
  const base = url.split('?')[0]
  return `${base}?v=${Date.now()}`
}

export interface PdfImportPageDetailsIllustrationsState {
  hiresAvailable: boolean | null
  illustrations: IllustrationRowState[]
  importId: number | null
  isDirty: boolean
  loading: boolean
  originalIllustrations: IllustrationRowState[]
  pageId: number | null
  saving: boolean
  selectedClientKey: string | null
}

const initialState: PdfImportPageDetailsIllustrationsState = {
  hiresAvailable: null,
  illustrations: [],
  importId: null,
  isDirty: false,
  loading: false,
  originalIllustrations: [],
  pageId: null,
  saving: false,
  selectedClientKey: null
}

const pdfImportPageDetailsIllustrations: Module<PdfImportPageDetailsIllustrationsState, any> = {
  namespaced: true,

  state: { ...initialState },

  getters: {
    hiresAvailable: (state) => state.hiresAvailable,
    illustrations: (state) => state.illustrations,
    illustrationsCanSave: (state) => state.isDirty,
    illustrationsIsDirty: (state) => state.isDirty,
    loading: (state) => state.loading,
    saving: (state) => state.saving,
    selectedClientKey: (state) => state.selectedClientKey,
    selectedIllustration: (state) =>
      state.illustrations.find((i) => i._clientKey === state.selectedClientKey) ?? null
  },

  mutations: {
    ADD_ILLUSTRATION(state, bounds: Bounds) {
      const row: IllustrationRowState = {
        id: null,
        sortOrder: 0,
        imageUrl: '',
        bounds: { ...bounds },
        _boundsChanged: true,
        _clientKey: generateClientKey(),
        _isNew: true,
        _previewUrl: null
      }
      state.illustrations.unshift(row)
      state.isDirty = true
      state.selectedClientKey = row._clientKey
    },

    APPLY_BOUNDS(state, { clientKey, bounds }: { clientKey: string; bounds: Bounds }) {
      const item = state.illustrations.find((i) => i._clientKey === clientKey)
      if (item) {
        item.bounds = { ...bounds }
        item._boundsChanged = true
        state.isDirty = true
      }
    },

    DELETE_ILLUSTRATION(state, clientKey: string) {
      const index = state.illustrations.findIndex((i) => i._clientKey === clientKey)
      if (index === -1) return

      const removed = state.illustrations[index]
      if (removed._previewUrl) URL.revokeObjectURL(removed._previewUrl)
      state.illustrations.splice(index, 1)
      state.isDirty = true

      if (state.illustrations.length === 0) {
        state.selectedClientKey = null
      } else {
        const nextIndex = Math.min(index, state.illustrations.length - 1)
        state.selectedClientKey = state.illustrations[nextIndex]._clientKey
      }
    },

    REORDER(state, newOrder: IllustrationRowState[]) {
      state.illustrations = newOrder
      state.isDirty = true
    },

    RESET_STATE(state) {
      for (const item of state.illustrations) {
        if (item._previewUrl) URL.revokeObjectURL(item._previewUrl)
      }
      Object.assign(state, { ...initialState, illustrations: [], originalIllustrations: [] })
    },

    REVERT_CHANGES(state) {
      for (const item of state.illustrations) {
        if (item._previewUrl) URL.revokeObjectURL(item._previewUrl)
      }
      state.illustrations = state.originalIllustrations.map((i) => ({
        ...i,
        bounds: { ...i.bounds },
        _boundsChanged: false,
        _previewUrl: null
      }))
      state.isDirty = false
      state.selectedClientKey = null
    },

    SET_HIRES_AVAILABLE(state, available: boolean) {
      state.hiresAvailable = available
    },

    SET_ILLUSTRATIONS(state, illustrations: IllustrationRowState[]) {
      state.illustrations = illustrations
      state.originalIllustrations = illustrations.map((i) => ({
        ...i,
        bounds: { ...i.bounds }
      }))
      state.isDirty = false
      // Clear selection if the previously selected key no longer exists
      const stillValid = illustrations.some((i) => i._clientKey === state.selectedClientKey)
      if (!stillValid) {
        state.selectedClientKey = null
      }
    },

    SET_IMPORT_ID(state, importId: number) {
      state.importId = importId
    },

    SET_LOADING(state, loading: boolean) {
      state.loading = loading
    },

    SET_PAGE_ID(state, pageId: number) {
      state.pageId = pageId
    },

    SET_PREVIEW_URL(state, { clientKey, url }: { clientKey: string; url: string | null }) {
      const item = state.illustrations.find((i) => i._clientKey === clientKey)
      if (item) item._previewUrl = url
    },

    SET_SAVING(state, saving: boolean) {
      state.saving = saving
    },

    SET_SELECTED(state, clientKey: string | null) {
      state.selectedClientKey = clientKey
    }
  },

  actions: {
    async addIllustration({ state, commit, dispatch }, bounds: Bounds) {
      commit('ADD_ILLUSTRATION', bounds)
      const illustration = state.illustrations[0]
      if (illustration && state.importId && state.pageId) {
        await dispatch('fetchIllustrationPreview', { illustration })
      }
    },

    async applyBounds(
      { state, commit, dispatch },
      { clientKey, bounds }: { clientKey: string; bounds: Bounds }
    ) {
      const existing = state.illustrations.find((i) => i._clientKey === clientKey)
      if (existing?._previewUrl) {
        URL.revokeObjectURL(existing._previewUrl)
        commit('SET_PREVIEW_URL', { clientKey, url: null })
      }
      commit('APPLY_BOUNDS', { clientKey, bounds })
      const illustration = state.illustrations.find((i) => i._clientKey === clientKey)
      if (illustration && state.importId && state.pageId) {
        await dispatch('fetchIllustrationPreview', { illustration })
      }
    },

    deleteIllustration({ state, commit }, clientKey: string) {
      commit('DELETE_ILLUSTRATION', clientKey)
      // Note: Parent count is updated only on save, not immediately
      // This matches BOM behavior where sidebar shows saved count, not draft count
    },

    async fetchIllustrationPreview(
      { state, commit },
      { illustration }: { illustration: IllustrationRowState }
    ) {
      if (!state.importId || !state.pageId) return
      try {
        // Unsaved rows have no illustration id — preview crops from the source page instead
        const blob = illustration.id == null
          ? await PdfImportController.getPagePreview(state.importId, state.pageId, illustration.bounds)
          : await PdfImportController.getIllustrationPreview(
            state.importId,
            illustration.id,
            illustration.bounds
          )
        commit('SET_PREVIEW_URL', { clientKey: illustration._clientKey, url: URL.createObjectURL(blob) })
      } catch {
        // Non-fatal — card falls back to imageUrl
      }
    },

    async initialize(
      { commit, dispatch },
      { importId, pageId }: { importId: number; pageId: number }
    ) {
      commit('RESET_STATE')
      commit('SET_IMPORT_ID', importId)
      commit('SET_PAGE_ID', pageId)
      await dispatch('loadIllustrations')
    },

    async loadIllustrations({ state, commit }) {
      if (!state.importId || !state.pageId) return

      try {
        commit('SET_LOADING', true)
        const response = await PdfImportController.getIllustrations(state.importId, state.pageId)
        const rows = response.illustrations.map((ill) =>
          illustrationToRowState({ ...ill, imageUrl: bustImageUrl(ill.imageUrl) })
        )
        commit('SET_ILLUSTRATIONS', rows)
        commit('pdfImportPageDetails/SET_LOCKED', response.locked, { root: true })
        // Note: Parent count is not updated on load - it's already correct from page info
        // Count is only updated after save to reflect persisted changes
      } catch (error: any) {
        postError({
          title: i18n.global.t('error'),
          text: i18n.global.t('pdfImportPageDetails.notificationLoadFailed')
        })
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    reorderIllustrations({ commit }, newOrder: IllustrationRowState[]) {
      commit('REORDER', newOrder)
    },

    revertChanges({ commit }) {
      commit('REVERT_CHANGES')
    },

    async saveIllustrations({ state, commit }) {
      if (!state.importId || !state.pageId) return

      try {
        commit('SET_SAVING', true)

        const request: IllustrationsSaveRequest = {
          illustrations: state.illustrations.map((item, index) => ({
            id: item.id,
            sortOrder: index,
            bounds: { ...item.bounds }
          }))
        }

        const response = await PdfImportController.saveIllustrations(
          state.importId,
          state.pageId,
          request
        )

        const rows = response.illustrations.map((ill) =>
          illustrationToRowState({ ...ill, imageUrl: bustImageUrl(ill.imageUrl) })
        )
        for (const item of state.illustrations) {
          if (item._previewUrl) URL.revokeObjectURL(item._previewUrl)
        }
        commit('SET_ILLUSTRATIONS', rows)
        commit(
          'pdfImportPageDetails/UPDATE_ILLUSTRATION_COUNT',
          rows.length,
          { root: true }
        )

        // Sync overview store with updated illustration count
        commit('pdfImportOverview/UPDATE_PAGE_DATA', {
          pageId: state.pageId,
          updates: { illustrationCount: rows.length }
        }, { root: true })

        postSuccess({
          title: i18n.global.t('success'),
          text: i18n.global.t('pdfImportPageDetails.notificationSaveSuccess')
        })
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : i18n.global.t('pdfImportPageDetails.notificationSaveFailed')
        })
        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    selectIllustration({ commit }, clientKey: string) {
      commit('SET_SELECTED', clientKey)
    },

    setHiresUnavailable({ commit }) {
      commit('SET_HIRES_AVAILABLE', false)
    }
  }
}

export default pdfImportPageDetailsIllustrations
